import { NodeUI } from 'modules/workflows/components/Node/types'
import { VariablesConfig } from 'modules/workflows/components/Sidepanel/VariableSelectorTypes'
import { WorkflowsExpression } from 'modules/workflows/lib/types'

/**
 * Parses a value string to extract entity IDs.
 * Handles both single values ("4") and JSON arrays ("[4,5]")
 */
function parseValueToIds(value: string): string[] {
  if (!value) return []

  // Try to parse as JSON array
  if (value.startsWith('[')) {
    try {
      const parsed = JSON.parse(value)
      if (Array.isArray(parsed)) {
        return parsed.map(String)
      }
    } catch {
      // Not valid JSON, treat as single value
    }
  }

  // Single value
  return [value]
}

/**
 * Extracts entity IDs from a single expression
 */
function extractIdsFromExpression(
  expression: WorkflowsExpression | undefined,
  variablesConfig: VariablesConfig,
  result: Record<string, string[]>
): void {
  if (!expression?.root?.expressions) return

  for (const comparison of expression.root.expressions) {
    const variableName = comparison.left?.name
    if (!variableName) continue

    const config = variablesConfig[variableName]
    if (!config || config.valueType !== 'entity') continue

    const entityType = config.entityType
    const value = comparison.right?.value
    if (!value) continue

    // Initialize array for this entity type if needed
    if (!result[entityType]) {
      result[entityType] = []
    }

    const ids = parseValueToIds(value)
    result[entityType].push(...ids)
  }
}

/**
 * Extracts all entity IDs from workflow nodes that need to be resolved.
 * Scans condition nodes and their comparisons to find entity values.
 *
 * This function is agnostic of specific entity types - it discovers them
 * dynamically from the variablesConfig.
 *
 * @param nodes - All workflow nodes
 * @param variablesConfig - Configuration mapping variable names to their entity types
 * @returns Record mapping entity type strings to arrays of IDs that need resolution
 */
export function extractEntityIdsFromWorkflow(
  nodes: NodeUI[],
  variablesConfig: VariablesConfig
): Record<string, string[]> {
  const result: Record<string, string[]> = {}

  for (const node of nodes) {
    // Check condition nodes (they have comparisons array)
    if (node.type === 'condition' && 'comparisons' in node.data) {
      for (const comparison of node.data.comparisons || []) {
        extractIdsFromExpression(comparison.expression, variablesConfig, result)
      }
    }

    // Check conditionIf nodes (they have a single comparison)
    if (node.type === 'conditionIf' && 'comparison' in node.data) {
      extractIdsFromExpression(node.data.comparison?.expression, variablesConfig, result)
    }
  }

  // Deduplicate IDs for each entity type
  const deduped: Record<string, string[]> = {}
  for (const [entityType, ids] of Object.entries(result)) {
    deduped[entityType] = [...new Set(ids)]
  }

  return deduped
}
